import type { AxiosError } from 'axios'
import api from './api'
import type {
  MonthlyPerformanceCommentary,
  MonthlyPerformanceCommentaryMeta,
  MonthlyPerformanceMonthSummary,
  MonthlyPerformanceReadiness,
  MonthlyPerformanceReport,
  MonthlyPerformanceProjectionCase,
  MonthlyPerformanceReportSummary,
  MonthlyPerformanceSettings,
  MonthlyPerformanceSendLogEntry,
} from '@/types/monthlyPerformance'

export class MonthlyPerformanceHttpError extends Error {
  status: number
  detail: Record<string, unknown>

  constructor(status: number, detail: Record<string, unknown>) {
    super(typeof detail.message === 'string' ? detail.message : 'Reporting request failed')
    this.status = status
    this.detail = detail
  }
}

async function wrap<T>(promise: Promise<{ data: T }>): Promise<T> {
  try {
    const response = await promise
    return response.data
  } catch (error) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as AxiosError<{ detail?: unknown }>
      const status = axiosError.response?.status ?? 500
      const rawDetail = axiosError.response?.data?.detail
      const detail =
        rawDetail && typeof rawDetail === 'object'
          ? (rawDetail as Record<string, unknown>)
          : { message: typeof rawDetail === 'string' ? rawDetail : 'Request failed' }
      throw new MonthlyPerformanceHttpError(status, detail)
    }
    throw error
  }
}

export async function getMonths(): Promise<MonthlyPerformanceMonthSummary[]> {
  return wrap(api.get('/monthly-performance/months'))
}

export async function getReadiness(month: string): Promise<MonthlyPerformanceReadiness> {
  return wrap(api.get('/monthly-performance/readiness', { params: { month } }))
}

export async function generateReport(
  month: string,
  projectionCaseId?: number | 'none',
): Promise<{ job_id: number; report_id: number }> {
  const body: { month: string; projection_case_id?: number | 'none' } = { month }
  if (projectionCaseId !== undefined) {
    body.projection_case_id = projectionCaseId
  }
  return wrap(api.post('/monthly-performance/generate', body))
}

export async function listProjectionCases(
  month: string,
): Promise<MonthlyPerformanceProjectionCase[]> {
  return wrap(api.get('/monthly-performance/projection-cases', { params: { month } }))
}

export async function listReports(month: string): Promise<MonthlyPerformanceReportSummary[]> {
  return wrap(api.get('/monthly-performance/reports', { params: { month } }))
}

export async function listSendsForMonth(
  month: string,
): Promise<MonthlyPerformanceSendLogEntry[]> {
  return wrap(api.get('/monthly-performance/sends', { params: { month } }))
}

export async function getReport(id: number): Promise<MonthlyPerformanceReport> {
  return wrap(api.get(`/monthly-performance/reports/${id}`))
}

export async function saveCommentary(
  id: number,
  commentary_final: MonthlyPerformanceCommentary,
): Promise<{ id: number; commentary_final: MonthlyPerformanceCommentary; html: string }> {
  return wrap(api.put(`/monthly-performance/reports/${id}/commentary`, { commentary_final }))
}

export async function previewCommentary(
  id: number,
  commentary_final: MonthlyPerformanceCommentary,
): Promise<{ html: string }> {
  return wrap(api.post(`/monthly-performance/reports/${id}/commentary/preview`, { commentary_final }))
}

export async function rewriteCommentary(
  id: number,
): Promise<{
  id: number
  commentary_draft: MonthlyPerformanceCommentary
  commentary: MonthlyPerformanceCommentaryMeta
  commentary_unverified: string[]
  html: string
}> {
  return wrap(api.post(`/monthly-performance/reports/${id}/commentary/rewrite`))
}

export async function acknowledgeCheck(
  id: number,
  key: string,
): Promise<{ id: number; acknowledged_checks: string[] }> {
  return wrap(api.post(`/monthly-performance/reports/${id}/checks/acknowledge`, { key }))
}

export async function getReportHtml(id: number): Promise<string> {
  const response = await api.get<string>(`/monthly-performance/reports/${id}/html`, {
    responseType: 'text',
    transformResponse: [(data) => data],
  })
  return response.data
}

export async function sendReport(
  id: number,
  mode: 'test' | 'members',
  email?: {
    send_as?: string
    from_name?: string
    reply_to?: string | null
    subject?: string
    recipients?: string[]
    cc?: string[]
    attach_comparison_pdf?: boolean
  },
): Promise<{
  report_id: number
  mode: string
  to: string[]
  cc: string[]
  gmail_message_id: string
}> {
  return wrap(api.post(`/monthly-performance/reports/${id}/send`, { mode, email }))
}

export async function setReportFinal(
  reportId: number,
  final: boolean,
): Promise<MonthlyPerformanceReportSummary[]> {
  return wrap(api.post(`/monthly-performance/reports/${reportId}/final`, { final }))
}

export async function setReportSent(
  reportId: number,
  sent: boolean,
): Promise<MonthlyPerformanceReport> {
  return wrap(api.post(`/monthly-performance/reports/${reportId}/sent`, { sent }))
}

export async function deleteDraft(id: number): Promise<{ deleted: boolean; id: number }> {
  return wrap(api.delete(`/monthly-performance/reports/${id}`))
}

export async function getSettings(): Promise<MonthlyPerformanceSettings> {
  return wrap(api.get('/monthly-performance/settings'))
}

export async function putSettings(
  body: Partial<MonthlyPerformanceSettings>,
): Promise<MonthlyPerformanceSettings> {
  return wrap(api.put('/monthly-performance/settings', body))
}
